import { type JackpotGameResponse, type JackpotGameMap, type GameJackpotUnion, GameJackpot } from '@game/types'
import { useQuery, useApi, useGamesJackpotsSubsriber, whenever, useCoreConfig, computed, toValue } from '#imports'
import type { MaybeRefOrGetter } from '#imports'

// The service fills in these English names when no label is configured; treat
// them as unset so the translated labels are used instead.
const DEFAULT_PRETTY_NAMES: Record<GameJackpotUnion, string> = {
    [GameJackpot.SUPER_JACKPOT]: 'Super Jackpot',
    [GameJackpot.SMALL_JACKPOT]: 'Jackpot',
    [GameJackpot.MEGAPRIZE]: 'Megaprize',
}

export const useGamesJackpots = (options: { enabled?: MaybeRefOrGetter<boolean> } = {}) => {
    const { site } = useCoreConfig()
    const api = useApi({ service: 'gameConfiguration' })

    function prepareResponse(jackpotsResponse: JackpotGameResponse[]) {
        const gameJackpotsMap: Record<string, JackpotGameMap> = jackpotsResponse?.reduce((acc: any, cur: JackpotGameResponse) => {
            const jackpot = cur.jackpots.find(x => x.type === GameJackpot.SMALL_JACKPOT)
            const superJackpot = cur.jackpots.find(x => x.type === GameJackpot.SUPER_JACKPOT)
            const megaprize = cur.jackpots.find(x => x.type === GameJackpot.MEGAPRIZE)
            const prettyName = cur.jackpots.find(x => x.jackpotPrettyName && x.jackpotPrettyName !== DEFAULT_PRETTY_NAMES[x.type])?.jackpotPrettyName

            acc[cur.slug] = {
                jackpot: jackpot?.value ?? 0,
                superJackpot: superJackpot?.value ?? 0,
                megaprize: megaprize?.value ?? 0,
                prettyName: prettyName ?? null,
            }

            return acc
        }, {})

        return gameJackpotsMap
    }

    // Jackpot updates will be received via websocket messages, ensuring up-to-date jackpot data.
    //
    // The async-data `key` shares this query's data across every caller, and the
    // query cache (getCachedData, default 10s TTL) lets lists that mount together
    // share a single fetch instead of one each. `enabled` is passed straight
    // through so jackpot-capable lists have it truthy at setup, taking the
    // cache-aware `immediate` load instead of a cache-bypassing manual refresh.
    const query = useQuery<ReturnType<typeof prepareResponse> | undefined>(async ({ signal }) => {
        const response = await api<JackpotGameResponse[]>('/gateway/game-configuration/public/jackpots', {
            signal,
            query: {
                brand: site.brand
            }
        })

        return prepareResponse(response)
    }, {
        key: 'game:games-jackpots',
        lazy: true,
        server: false,
        enabled: () => toValue(options.enabled ?? true),
        deep: true,
    })

    const gamesJackpots = computed(() => query.data.value)

    if (import.meta.client) {
        const { jackpots } = useGamesJackpotsSubsriber()

        whenever(jackpots, (jackpotsSubscribeResult: { [gameSlug: string]: number }) => {
            Object.entries(jackpotsSubscribeResult).forEach(([gameSlug, amount]) => {
                if (query.data.value?.[gameSlug]) {
                    query.data.value[gameSlug].superJackpot = amount
                }
            })
        }, {
            immediate: true,
            deep: true,
        })
    }

    return query.enrich({
        gamesJackpots,
    })
}
